/**
 * Recurring Job table column policy.
 *
 * Three columns the metadata type alone cannot express: `turnOn` is the running switch and reads
 * On/Off rather than True/False, `schedule` is an array of recurrence rules that has to be spelled
 * out per row, and `start`/`end` are one period, so `start` renders both and `end` folds into it.
 * Every other column keeps the framework's ValueCellRenderer.
 */
import { MetadataEntityData, MetadataEntityFieldData, RecurringJob } from '@jms/model';
import type { EntityBoardColumnOverrides, EntityBoardContext } from '@workbuddy/ui/v2/blocks/entity.board/context';
import { BooleanCellParams, BooleanCellRenderer, DateRangeCellParams, DateRangeCellRenderer, RecurringJobScheduleCellRenderer } from '@workbuddy/ui/cell.renderers';

/**
 * `schedule` stacks one block per recurrence rule, each up to two lines, so it is the column that
 * defines the row height. autoHeight is opt-in on that one column, never board-wide: AG gives an
 * auto-height cell no height of its own, so every other cell would stop centring and the grid would
 * lose column virtualisation.
 *
 * `end` and `createdBy` are hidden rather than dropped from the view: the view is shared with the
 * legacy /jobs/recurring board, so this board states its own column set. `end` is still rendered —
 * inside the `start` cell — and `createdBy` is audit data the row does not need to carry.
 */
export const recurringJobColumnOverrides: EntityBoardColumnOverrides<RecurringJob> = {
  byField: {
    schedule: { flex: 2, minWidth: 280, autoHeight: true },
    start: { headerName: 'Start - End', minWidth: 180 },
    end: { hide: true },
    createdBy: { hide: true },
  },
};

export function overrideRecurringJobCellRenderer(_context: EntityBoardContext<RecurringJob>, _metadata: MetadataEntityData, field: MetadataEntityFieldData, renderer: unknown): unknown {

  switch (field.path as keyof RecurringJob) {
    case 'turnOn':
      return { cellRenderer: BooleanCellRenderer, cellRendererParams: { format: 'On/Off' } satisfies Partial<BooleanCellParams> };
    case 'schedule':
      // `field` must be forwarded: wb-value falls back to a bare stringify without it, so an empty
      // schedule would render blank rather than Not Set.
      return { cellRenderer: RecurringJobScheduleCellRenderer, cellRendererParams: { field } };
    case 'start':
      return { cellRenderer: DateRangeCellRenderer, cellRendererParams: { endPath: 'end', field } satisfies Partial<DateRangeCellParams> };
    default:
      return renderer;
  }
}
